import React, { useCallback, useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { formatIdDateTime } from "@/lib/format";
import { CheckCircle, XCircle, ClockClockwise, Scales, Warning } from "@phosphor-icons/react";
import { useAuth } from "@/context/AuthContext";
import DecisionDialog from "@/components/approval/DecisionDialog";
import SoAdjustmentSubmitForm from "@/components/approval/SoAdjustmentSubmitForm";

/**
 * Modal for submitting + reviewing SO kuantiti adjustments.
 * Admin Kantor & Admin can propose a delta (+ or -) with a reason.
 * Super Admin (role='admin') sees Approve / Reject buttons on pending items.
 *
 * Props:
 *   so:       current SO doc ({ id, no_so, kuantiti_total, kuantiti_sisa })
 *   open:     boolean
 *   onClose:  () => void
 *   onApplied: () => void  — called after approve/reject so parent can reload list
 */
export default function SoAdjustmentModal({ so, open, onClose, onApplied }) {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [deltaStr, setDeltaStr] = useState("");
  const [reason, setReason] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [decision, setDecision] = useState(null); // { id, action }

  const isSuperAdmin = user?.role === "admin";
  const canSubmit = user?.role === "admin" || user?.role === "admin_kantor";

  const load = useCallback(async () => {
    if (!so?.id) return;
    setLoading(true);
    try {
      const r = await api.get(`/sales-orders/${so.id}/adjustments`);
      setItems(r.data || []);
    } catch (e) {
      // Silent
    } finally {
      setLoading(false);
    }
  }, [so?.id]);

  useEffect(() => {
    if (open) { load(); setDeltaStr(""); setReason(""); setDecision(null); }
  }, [open, load]);

  const pending = items.find((c) => c.status === "pending");

  const submit = async () => {
    const delta = Number(deltaStr);
    if (!delta || Math.abs(delta) < 0.001) {
      toast.error("Delta tidak boleh 0");
      return;
    }
    if (reason.trim().length < 3) {
      toast.error("Alasan wajib diisi (minimal 3 karakter)");
      return;
    }
    setSubmitting(true);
    try {
      await api.post(`/sales-orders/${so.id}/adjustments`, {
        delta, reason: reason.trim(),
      });
      toast.success("Adjustment diajukan — menunggu approval Super Admin");
      setDeltaStr(""); setReason("");
      await load();
      onApplied && onApplied();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSubmitting(false);
    }
  };

  const decide = async (note) => {
    if (!decision) return;
    setSubmitting(true);
    try {
      const r = await api.post(
        `/sales-orders/adjustments/${decision.id}/${decision.action}`,
        { note },
      );
      if (decision.action === "approve") {
        const after = r.data?.after || {};
        const warn = r.data?.sisa_negative;
        toast.success(warn
          ? `Adjustment applied — sisa jadi ${after.kuantiti_sisa} (over-quota!)`
          : `Adjustment applied — total ${after.kuantiti_total}, sisa ${after.kuantiti_sisa}`);
      } else {
        toast.success("Adjustment ditolak");
      }
      setDecision(null);
      await load();
      onApplied && onApplied();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSubmitting(false);
    }
  };

  const cancelDraft = async (aid) => {
    if (!window.confirm("Batalkan draft adjustment ini?")) return;
    try {
      await api.delete(`/sales-orders/adjustments/${aid}`);
      toast.success("Draft dibatalkan");
      await load();
      onApplied && onApplied();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl rounded-none" data-testid="so-adjustment-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Scales size={20} weight="bold" /> Penyesuaian Kuantiti SO
          </DialogTitle>
          <DialogDescription>
            {so?.no_so && <span className="font-mono font-bold">{so.no_so}</span>}
            {" — Total sekarang: "}
            <span className="font-mono font-bold">{so?.kuantiti_total ?? "-"}</span>
            {" · Sisa: "}
            <span className="font-mono font-bold">{so?.kuantiti_sisa ?? "-"}</span>
          </DialogDescription>
        </DialogHeader>

        {canSubmit && !pending && (
          <SoAdjustmentSubmitForm
            so={so}
            deltaStr={deltaStr}
            reason={reason}
            onDeltaChange={setDeltaStr}
            onReasonChange={setReason}
            submitting={submitting}
            onSubmit={submit}
          />
        )}

        {/* History list */}
        <div className="mt-4">
          <div className="overline text-xs mb-2">Riwayat Penyesuaian</div>
          {loading ? (
            <div className="p-4 text-sm text-muted-foreground">Memuat…</div>
          ) : items.length === 0 ? (
            <div className="p-4 text-sm text-muted-foreground border border-dashed border-border">
              Belum ada penyesuaian.
            </div>
          ) : (
            <div className="divide-y divide-border max-h-[300px] overflow-y-auto border border-border">
              {items.map((c) => (
                <AdjustmentRow
                  key={c.id}
                  c={c}
                  isSuperAdmin={isSuperAdmin}
                  currentEmail={user?.email}
                  onDecide={(action) => setDecision({ id: c.id, action })}
                  onCancelDraft={() => cancelDraft(c.id)}
                />
              ))}
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" className="rounded-none" onClick={onClose}>Tutup</Button>
        </DialogFooter>

        <DecisionDialog
          open={!!decision}
          onClose={() => setDecision(null)}
          action={decision?.action}
          title={decision?.action === "approve" ? "Setujui Penyesuaian" : "Tolak Penyesuaian"}
          description={decision?.action === "approve"
            ? "Delta akan diterapkan ke kuantiti_total. Sisa auto-derive."
            : "Draft ditolak, SO tidak berubah."}
          onConfirm={decide}
          submitting={submitting}
          testIdPrefix="so-adjustment"
        />
      </DialogContent>
    </Dialog>
  );
}

function AdjustmentRow({ c, isSuperAdmin, currentEmail, onDecide, onCancelDraft }) {
  const badge = c.status === "pending"
    ? { cls: "bg-amber-200 text-amber-900", label: "PENDING" }
    : c.status === "approved"
      ? { cls: "bg-emerald-200 text-emerald-900", label: "APPROVED" }
      : { cls: "bg-rose-200 text-rose-900", label: "REJECTED" };
  const isOwnDraft = c.status === "pending" && c.submitted_by?.email === currentEmail;
  const delta = Number(c.delta || 0);

  return (
    <div className="p-3 text-sm space-y-1" data-testid={`adjustment-row-${c.id}`}>
      <div className="flex items-center gap-2 flex-wrap">
        <span className={`px-2 py-0.5 text-[10px] font-mono rounded-full ${badge.cls}`}>{badge.label}</span>
        <span className="font-mono font-bold">
          {delta > 0 ? "+" : ""}{delta}
        </span>
        <span className="text-xs text-muted-foreground">
          oleh {c.submitted_by?.name || c.submitted_by?.email} · {formatIdDateTime(c.submitted_at)}
        </span>
      </div>
      {c.reason && <div className="text-xs italic text-muted-foreground">"{c.reason}"</div>}
      {c.status === "approved" && c.after_snapshot?.kuantiti_total !== undefined && (
        <div className="text-xs font-mono">
          → Total: <strong>{c.after_snapshot.kuantiti_total}</strong>, Sisa: <strong>{c.after_snapshot.kuantiti_sisa}</strong>
          {c.sisa_negative_at_approve && (
            <span className="ml-2 text-amber-700 font-semibold">
              <Warning size={12} className="inline" weight="fill" /> Sisa negatif (over-quota)
            </span>
          )}
        </div>
      )}
      {c.decided_by && (
        <div className="text-xs text-muted-foreground border-t border-border pt-1">
          {c.status === "approved" ? <CheckCircle size={12} className="inline text-emerald-700 mr-1" />
            : <XCircle size={12} className="inline text-rose-700 mr-1" />}
          {c.status === "approved" ? "Disetujui" : "Ditolak"} oleh <span className="font-semibold">{c.decided_by?.name || c.decided_by?.email}</span>
          {" · "}{formatIdDateTime(c.decided_at)}
          {c.decision_note && <span> — "{c.decision_note}"</span>}
        </div>
      )}
      {c.status === "pending" && (
        <div className="flex items-center gap-2 pt-1">
          {isSuperAdmin && (
            <>
              <Button size="sm" onClick={() => onDecide("approve")} data-testid={`approve-${c.id}`}
                      className="rounded-none">
                <CheckCircle size={14} className="mr-1" /> Setujui
              </Button>
              <Button size="sm" variant="destructive" onClick={() => onDecide("reject")}
                      data-testid={`reject-${c.id}`} className="rounded-none">
                <XCircle size={14} className="mr-1" /> Tolak
              </Button>
            </>
          )}
          {isOwnDraft && (
            <Button size="sm" variant="ghost" onClick={onCancelDraft} data-testid={`cancel-${c.id}`}
                    className="rounded-none">
              <ClockClockwise size={14} className="mr-1" /> Batalkan
            </Button>
          )}
        </div>
      )}
    </div>
  );
}
